import { useEffect, useRef } from 'react';
import useEventBus from './useEventBus';

/**
 * Custom hook to track and manage payment information from UDS events.
 * 
 * Listens to:
 * - uds:PaymentCalcResponse:Update - Updates payment calculation data
 * - uds:PaymentObject:Update - Updates downPayment from payment objects
 * 
 * @returns {React.RefObject} - A ref object containing current payment info
 */
const usePaymentsInfo = () => {
	const [eventBus, isReady] = useEventBus();
	const paymentsInfoRef = useRef(null);

	useEffect(() => {
		if (isReady) {
			const handleCalcResponse = (data) => {
				if (data?.creditType) {
					paymentsInfoRef.current = {
						...paymentsInfoRef.current,
						creditType: data.creditType,
						...(data.dealType && { dealType: data.dealType }),
						...(data.loanTerm && { loanTerm: data.loanTerm }),
						...(data.leaseTerm && { leaseTerm: data.leaseTerm }),
						tradeIn: data.tradeIn || {
							value: null,
							amountOwed: null,
							condition: null,
							source: null,
							ico: []
						},
						icoTradeIn: data.icoTradeIn || {},
						penciling: data.penciling || {}
					};
				}
			};

			const handlePaymentObjectUpdate = (paymentObjects) => {
				if (!Array.isArray(paymentObjects) || !paymentsInfoRef.current) return;
				const dealType = paymentsInfoRef.current.dealType || 'finance';
				const match = paymentObjects.find((p) => p.paymentLabel === dealType);
				if (match?.downPayment != null) {
					paymentsInfoRef.current = {
						...paymentsInfoRef.current,
						downPayment: match.downPayment
					};
				}
			};

			eventBus.on('uds:PaymentCalcResponse:Update', handleCalcResponse);
			eventBus.on('uds:PaymentObject:Update', handlePaymentObjectUpdate);

			return () => {
				eventBus.off('uds:PaymentCalcResponse:Update', handleCalcResponse);
				eventBus.off('uds:PaymentObject:Update', handlePaymentObjectUpdate);
			};
		}
		return undefined;
	}, [eventBus, isReady]);

	return paymentsInfoRef;
};

export default usePaymentsInfo;
